import {
  Button,
  Card,
  CircularProgress,
  Grid,
  InputLabel,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import React, { useEffect } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import TextFieldWrapper from "../../components/FormComponents/TextFieldWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import AuthQuery from "../../stateQueries/Auth";
import AlertPopup from "../../components/AlertPopup";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const ResetPassword = ({ currentTheme }) => {
  let { resetToken } = useParams();
  const navigate = useNavigate();

  const { mutate, isLoading, isError } = useMutation({
    mutationFn: async (formData) => {
      return await AuthQuery.verifyResetToken(formData);
    },
    onSuccess: (data) => {
      console.log(data);
      resetToken = null;
    },
    onError: (err) => {
      console.log(err);
      resetToken = null;
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (formData) => {
      return AuthQuery.resetUserPassword(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    }
  });

  useEffect(() => {
    mutate({ resetToken: resetToken });
  }, [mutate, resetToken]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #3a9bfb 0%, #163683 50%, #0d2456 100%)"
        }}
      >
        <CircularProgress sx={{ color: "white" }} size={60} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Stack
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #3a9bfb 0%, #163683 50%, #0d2456 100%)",
          justifyContent: "center",
          alignItems: "center",
          p: 2
        }}
      >
        <Card
          sx={{
            p: 4,
            maxWidth: "500px",
            width: "100%",
            borderRadius: "24px",
            background:
              currentTheme === "light"
                ? "rgba(255, 255, 255, 0.95)"
                : "rgba(30, 30, 46, 0.95)",
            backdropFilter: "blur(20px)",
            textAlign: "center"
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="error" mb={2}>
            Link Expired
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            This password reset link has expired. Please request a new one.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/auth/forgot-password")}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              py: 1.5
            }}
          >
            Request New Link
          </Button>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #3a9bfb 0%, #163683 50%, #0d2456 100%)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Left Side - Branding */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          zIndex: 1
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            animation: "floatAnimation 3s ease-in-out infinite"
          }}
        >
          <Box
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "30px",
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 2rem",
              border: "2px solid rgba(255,255,255,0.3)"
            }}
          >
            <LockResetIcon sx={{ fontSize: 60, color: "white" }} />
          </Box>
          <Typography
            variant="h3"
            sx={{
              color: "white",
              fontWeight: 700,
              mb: 2,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)"
            }}
          >
            Create New Password
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.95)",
              maxWidth: "400px",
              lineHeight: 1.6
            }}
          >
            Your new password must be different from previously used passwords
          </Typography>
        </Box>
      </Stack>

      {/* Right Side - Form */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{ position: "relative", zIndex: 1, px: 2 }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: "480px",
            p: 5,
            background:
              currentTheme === "light"
                ? "rgba(255, 255, 255, 0.95)"
                : "rgba(30, 30, 46, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            boxShadow:
              currentTheme === "light"
                ? "0 20px 60px rgba(0,0,0,0.15)"
                : "0 20px 60px rgba(0,0,0,0.5)",
            border: "1px solid",
            borderColor:
              currentTheme === "light"
                ? "rgba(255,255,255,0.5)"
                : "rgba(255,255,255,0.1)",
            position: "relative"
          }}
        >
          {resetPasswordMutation.isSuccess && (
            <AlertPopup
              open={true}
              message={resetPasswordMutation.data?.message}
            />
          )}

          {resetPasswordMutation.isError && (
            <AlertPopup
              open={true}
              message={
                resetPasswordMutation.error?.response?.data?.message ||
                "Server Error"
              }
              severity="error"
            />
          )}

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: currentTheme === "light" ? "text.primary" : "white",
                mb: 1
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color:
                  currentTheme === "light"
                    ? "text.secondary"
                    : "rgba(255,255,255,0.7)"
              }}
            >
              Enter your new password below
            </Typography>
          </Box>

          <Formik
            initialValues={{
              password: "",
              confirmPassword: "",
              resetToken: resetToken
            }}
            validationSchema={Yup.object().shape({
              password: Yup.string()
                .min(8, "Password must be at least 8 characters")
                .required("Password required"),
              confirmPassword: Yup.string()
                .required("Confirm password required")
                .oneOf([Yup.ref("password")], "Passwords do not match")
            })}
            onSubmit={(values) => {
              resetPasswordMutation.mutate(values);
            }}
          >
            {() => {
              return (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <InputLabel
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color:
                            currentTheme === "light" ? "text.primary" : "white"
                        }}
                      >
                        New Password
                      </InputLabel>
                      <TextFieldWrapper
                        name="password"
                        placeholder="Enter new password"
                        type="password"
                        fullWidth
                        startIcon={<LockIcon sx={{ color: "primary.main" }} />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              currentTheme === "light"
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                currentTheme === "light"
                                  ? "rgba(0,0,0,0.04)"
                                  : "rgba(255,255,255,0.08)"
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <InputLabel
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color:
                            currentTheme === "light" ? "text.primary" : "white"
                        }}
                      >
                        Confirm Password
                      </InputLabel>
                      <TextFieldWrapper
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        type="password"
                        fullWidth
                        startIcon={
                          <LockOpenIcon sx={{ color: "primary.main" }} />
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              currentTheme === "light"
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                currentTheme === "light"
                                  ? "rgba(0,0,0,0.04)"
                                  : "rgba(255,255,255,0.08)"
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={resetPasswordMutation.isLoading}
                        sx={{
                          py: 1.5,
                          borderRadius: "12px",
                          fontSize: "16px",
                          fontWeight: 600,
                          textTransform: "none",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          boxShadow: "0 8px 24px rgba(102,126,234,0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 32px rgba(102,126,234,0.5)"
                          },
                          "&:disabled": {
                            background: "rgba(0,0,0,0.12)"
                          }
                        }}
                      >
                        {resetPasswordMutation.isLoading ? (
                          <CircularProgress color="inherit" size={24} />
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center" }}>
                        <Button
                          onClick={() => navigate("/login")}
                          sx={{
                            textTransform: "none",
                            color: "primary.main",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline"
                            }
                          }}
                        >
                          Back to Login
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </Stack>

      <style>
        {`
          @keyframes floatAnimation {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}
      </style>
    </Stack>
  );
};

export default ResetPassword;
